package com.exura.features.plandefinition.domain.builder.series

import com.exura.features.plandefinition.domain.builder.BuilderField
import com.exura.features.plandefinition.domain.model.element.QuestionElement
import com.exura.features.plandefinition.domain.model.series.SeriesSpawnType
import com.exura.features.plandefinition.domain.model.series.SimpleQuestionSeries

class SimpleQuestionSeriesBuilderNew : BaseAnchoredSeriesBuilder<QuestionElement, SimpleQuestionSeries>(
    SeriesSpawnType.QUESTION
) {

    private val duration = BuilderField(isRequired = true, default = 1)
    private val question = BuilderField<String>(isRequired = true)
    private val options = BuilderField<MutableList<SimpleQuestionSeries.QuestionOption>>(
        default = mutableListOf()
    )
    private val estimatedMinutes = BuilderField(isRequired = true, default = 5)

    fun duration(): Int? = duration.getValue()
    fun setDuration(newValue: Int) = apply {
        val res = duration.setValue(newValue)
        updateChangedState(res)
        updateBuildState(res)
        updateElementsCalculationState(res)
    }

    fun question(): String? = question.getValue()
    fun setQuestion(newValue: String) = apply {
        val res = question.setValue(newValue)
        updateChangedState(res)
        updateBuildState(res)
        updateElementsCalculationState(res)
    }

    fun options(): List<SimpleQuestionSeries.QuestionOption> = options.getValue()?.toList() ?: emptyList()

    fun addOption(id: String, text: String, responseMessage: String? = null, isResponsePositive: Boolean = true) = apply {
        val currentList = options.getValue()!!
        val newOption = SimpleQuestionSeries.QuestionOption(id, text, responseMessage, isResponsePositive)

        currentList.add(newOption)
        options.markChanged()
        updateChangedState(true)
        updateBuildState(true)
        updateElementsCalculationState(true)
    }

    fun updateOption(optionIndex: Int, newOption: SimpleQuestionSeries.QuestionOption?) = apply {
        val currentList = options.getValue()!!

        if (newOption != null && optionIndex in currentList.indices) {
            val existingOption = currentList[optionIndex]

            // Only update if the option is actually different
            if (existingOption != newOption) {
                currentList[optionIndex] = newOption
                options.markChanged()
                updateChangedState(true)
                updateBuildState(true)
                updateElementsCalculationState(true)
            }
        }
    }

    fun removeOption(optionIndex: Int) = apply {
        val currentList = options.getValue()!!

        if (optionIndex in currentList.indices) {
            currentList.removeAt(optionIndex)
            options.markChanged()
            updateChangedState(true)
            updateBuildState(true)
            updateElementsCalculationState(true)
        }
    }

    fun setOptions(newOptions: List<SimpleQuestionSeries.QuestionOption>) = apply {
        val currentList = options.getValue() ?: mutableListOf()
        currentList.clear()
        currentList.addAll(newOptions)
        val res = options.setValue(currentList)
        updateChangedState(res)
        updateBuildState(res)
        updateElementsCalculationState(res)
    }

    fun estimatedMinutes(): Int? = estimatedMinutes.getValue()
    fun setEstimatedMinutes(newValue: Int) = apply {
        val res = estimatedMinutes.setValue(newValue)
        updateChangedState(res)
        updateBuildState(res)
        updateElementsCalculationState(res)
    }

    override fun markSaved() = apply {
        duration.markSaved()
        question.markSaved()
        options.markSaved()
        estimatedMinutes.markSaved()
        super.markSaved()
    }

    override fun validate(): List<String> {
        val fields = mapOf(
            "duration" to duration,
            "question" to question,
            "options" to options,
            "estimatedMinutes" to estimatedMinutes,
        )
        val messages = validateFields(fields).toMutableList()
        // Ensure one and only one duration specification is provided
        val optionsList = options.getValue() ?: emptyList()
        if (optionsList.isEmpty()) {
            messages.add("Question must have at least one option")
        }
        return messages + super.validate()
    }

    override fun build(): SimpleQuestionSeries {
        return SimpleQuestionSeries(
            firebaseDocId = firebaseDocId(),
            firebaseDocPrefix = null, // Will be set when saved to Firebase
            title = name() ?: "",
            description = description() ?: "",
            relativeDay = relativeDay() ?: 0,
            duration = duration() ?: 1,
            question = question() ?: "",
            options = options(),
            imageUrl = imageUrl(),
            estimatedMinutes = estimatedMinutes() ?: 5
        )
    }
}

fun SimpleQuestionSeries.toBuilder(): SimpleQuestionSeriesBuilderNew {
    val series = this
    return SimpleQuestionSeriesBuilderNew().apply {
        setFirebaseDocId(series.firebaseDocId)
        setName(series.title)
        setDescription(series.description)
        setImageUrl(series.imageUrl)
        setRelativeDay(series.relativeDay)
        setDuration(series.durationInDays())
        setQuestion(series.question)
        setOptions(series.options)
        setEstimatedMinutes(series.estimatedMinutes)
        markSaved()
    }
}